import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Mail, ShieldCheck, AlertCircle, Lock } from 'lucide-react';
import clsx from 'clsx';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { apiCall } from '../../services/api.service';
import { toastError } from '../../features/clu/utils/notify';

export interface StepUpRequest {
  level: number;
  onVerified: () => void;
  onCancelled: () => void;
}

export interface StepUpModalProps {
  request: StepUpRequest | null;
}

type StepUpStatus = 'sending' | 'awaiting_code' | 'verifying' | 'locked' | 'verified';

interface VerifyErrorDetail {
  error: 'invalid_code' | 'code_expired_or_not_found' | 'max_attempts_exceeded';
  attempts_remaining: number;
  blocked: boolean;
}

const CODE_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 30;
const SPAM_HINT_THRESHOLD = 3;

export function StepUpModal({ request }: StepUpModalProps) {
  const [status, setStatus] = useState<StepUpStatus>('sending');
  const [target, setTarget] = useState<string>('');
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [codeExpired, setCodeExpired] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const [resendCount, setResendCount] = useState(0);

  const cooldownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const clearCooldownInterval = useCallback(() => {
    if (cooldownIntervalRef.current) {
      clearInterval(cooldownIntervalRef.current);
      cooldownIntervalRef.current = null;
    }
  }, []);

  const startCooldown = useCallback(() => {
    setCooldownSeconds(RESEND_COOLDOWN_SECONDS);
    clearCooldownInterval();
    cooldownIntervalRef.current = setInterval(() => {
      setCooldownSeconds((prev) => {
        if (prev <= 1) {
          clearCooldownInterval();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, [clearCooldownInterval]);

  const requestCode = useCallback(
    async (level: number, { isResend = false }: { isResend?: boolean } = {}) => {
      setStatus('sending');
      setErrorMessage(null);
      setCodeExpired(false);
      try {
        const response = await apiCall(
          '/api/auth/step-up/request',
          'POST',
          { level },
          false,
          { skipStepUp: true }
        );
        setTarget(response?.target || '');
        setCode('');
        setStatus('awaiting_code');
        if (isResend) {
          setResendCount((prev) => prev + 1);
        }
        startCooldown();
      } catch (err: any) {
        toastError(
          'Could not send verification code',
          err?.message || 'Please try again in a moment.'
        );
        setStatus('awaiting_code');
      }
    },
    [startCooldown]
  );

  useEffect(() => {
    if (request) {
      setResendCount(0);
      requestCode(request.level);
    } else {
      clearCooldownInterval();
      setStatus('sending');
      setTarget('');
      setCode('');
      setErrorMessage(null);
      setCodeExpired(false);
      setCooldownSeconds(0);
      setResendCount(0);
    }
    return () => {
      clearCooldownInterval();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request]);

  const verifyCode = useCallback(
    async (submittedCode: string) => {
      if (!request) return;
      setStatus('verifying');
      setErrorMessage(null);
      try {
        await apiCall(
          '/api/auth/step-up/verify',
          'POST',
          { level: request.level, code: submittedCode },
          false,
          { skipStepUp: true }
        );
        setStatus('verified');
        setTimeout(() => {
          request.onVerified();
        }, 550);
      } catch (err: any) {
        const detail: VerifyErrorDetail | undefined =
          err?.rawDetail && typeof err.rawDetail === 'object' ? err.rawDetail : undefined;

        if (detail?.error === 'max_attempts_exceeded' || detail?.blocked) {
          setStatus('locked');
          return;
        }

        if (detail?.error === 'code_expired_or_not_found') {
          setCodeExpired(true);
          setErrorMessage('This code has expired. Send a new one.');
          setStatus('awaiting_code');
          return;
        }

        if (detail?.error === 'invalid_code') {
          setErrorMessage(
            `That code isn't right. You have ${detail.attempts_remaining} attempt(s) left.`
          );
          setCode('');
          setStatus('awaiting_code');
          inputRef.current?.focus();
          return;
        }

        // Unexpected shape (network error, 5xx, etc.) — outside the normal flow.
        toastError('Verification failed', err?.message || 'Please try again.');
        setStatus('awaiting_code');
      }
    },
    [request]
  );

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digitsOnly);
    if (digitsOnly.length === CODE_LENGTH) {
      verifyCode(digitsOnly);
    }
  };

  const handleResend = () => {
    if (!request || cooldownSeconds > 0) return;
    requestCode(request.level, { isResend: true });
  };

  const handleClose = () => {
    if (!request) return;
    request.onCancelled();
  };

  if (!request) {
    return null;
  }

  return (
    <Dialog open={!!request} onClose={status === 'locked' ? handleClose : () => {}} size="sm">
      <DialogHeader>
        <DialogTitle>Confirm it's you</DialogTitle>
      </DialogHeader>
      <DialogContent className="max-h-[85dvh] overflow-y-auto">
        {status === 'locked' ? (
          <div className="flex flex-col items-center gap-3 py-4 text-center">
            <Lock className="h-10 w-10 text-red-600" />
            <p className="text-sm text-ink">
              Too many incorrect attempts. Try again in 30 minutes.
            </p>
          </div>
        ) : status === 'verified' ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center transition-opacity duration-500">
            <ShieldCheck className="h-10 w-10 text-blue-600" />
            <p className="text-sm text-ink">Verified.</p>
          </div>
        ) : status === 'sending' ? (
          <div className="flex flex-col items-center gap-3 py-6 text-center">
            <Mail className="h-10 w-10 text-blue-600 animate-pulse" />
            <p className="text-sm text-ink-muted">Sending your verification code…</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3">
              <Mail className="h-5 w-5 text-blue-600 shrink-0 mt-0.5" />
              <p className="text-sm text-ink">
                We sent a 6-digit code to <span className="font-semibold">{target}</span>. Enter it
                below to confirm this change.
              </p>
            </div>

            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={CODE_LENGTH}
              autoFocus
              disabled={codeExpired || status === 'verifying'}
              value={code}
              onChange={handleCodeChange}
              placeholder="000000"
              className={clsx(
                'h-12 w-full rounded-md border border-line bg-surface px-4 text-center text-lg tracking-[0.5em] text-ink',
                'focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-blue-600',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            />

            {errorMessage && (
              <div className="flex items-start gap-2 text-sm text-red-600">
                <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
                <span>{errorMessage}</span>
              </div>
            )}

            <Button
              variant="primary"
              className="h-12 w-full"
              loading={status === 'verifying'}
              disabled={code.length !== CODE_LENGTH || status === 'verifying'}
              onClick={() => verifyCode(code)}
            >
              Confirm
            </Button>

            <div className="flex flex-col items-center gap-1">
              <button
                type="button"
                onClick={handleResend}
                disabled={cooldownSeconds > 0}
                className={clsx(
                  'h-12 text-sm font-medium underline-offset-2',
                  cooldownSeconds > 0
                    ? 'text-ink-muted cursor-not-allowed'
                    : 'text-blue-600 hover:text-blue-700 hover:underline'
                )}
              >
                {cooldownSeconds > 0 ? `Resend code (${cooldownSeconds}s)` : 'Resend code'}
              </button>
              {resendCount >= SPAM_HINT_THRESHOLD && (
                <p className="text-xs text-ink-muted">
                  Still not receiving it? Check your spam folder.
                </p>
              )}
            </div>
          </div>
        )}
      </DialogContent>
      {status === 'locked' && (
        <DialogFooter>
          <Button variant="secondary" className="h-12" onClick={handleClose}>
            Close
          </Button>
        </DialogFooter>
      )}
    </Dialog>
  );
}

export default StepUpModal;
